import { useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useTheme } from '@mui/material/styles';
import { traverseFileTree } from '../utils/fileHelpers';

export default function CompactUploadDropzone({ onFilesUploaded }) {
  const theme = useTheme();
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'chemical/x-pdb': ['.pdb'],
      'application/octet-stream': ['.pdb']
    },
    useFsAccessApi: false,
    noClick: true,
    getFilesFromEvent: useCallback(async (event) => {
      const files = [];
      
      if (event.dataTransfer) {
        // Collect all entries synchronously first (DataTransferItemList becomes invalid after async)
        const entries = [];
        const items = Array.from(event.dataTransfer.items);
        
        // Collect entries synchronously
        for (const item of items) {
          if (item.kind === 'file') {
            const entry = item.webkitGetAsEntry();
            if (entry) {
              entries.push(entry);
            }
          }
        }
        
        // Now process entries asynchronously
        for (const entry of entries) {
          await traverseFileTree(entry, '', files);
        }
        
        return files;
      }
      
      return Array.from(event.target.files || []);
    }, []),
    onDrop: useCallback((acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        onFilesUploaded(acceptedFiles);
      }
    }, [onFilesUploaded])
  });

  const handleDirectorySelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    onFilesUploaded(files);
  }, [onFilesUploaded]);

  const handleDropzoneClick = useCallback(() => {
    document.getElementById('pdb-directory-input-compact').click();
  }, []);


  return (
    <Box sx={{ height: '100%' }}>
      <input
        type="file"
        webkitdirectory=""
        onChange={handleDirectorySelect}
        style={{ display: 'none' }}
        id="pdb-directory-input-compact"
      />

      <Box
        {...getRootProps()}
        onClick={handleDropzoneClick}
        sx={{
          height: '100%',
          border: '1px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 1,
          backgroundColor: isDragActive ? 'primary.50' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: theme.spacing(0.5),
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          px: theme.spacing(1),
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover'
          }
        }}
      >
        <input {...getInputProps()} />
        
        <CloudUploadIcon 
          sx={{ 
            fontSize: theme.dimensions.icon.small, 
            color: isDragActive ? 'primary.main' : theme.palette.grey[400]
          }} 
        />
        
        <Typography 
          variant="caption" 
          color={isDragActive ? 'primary.main' : 'text.primary'}
          sx={{ 
            fontWeight: isDragActive ? theme.typography.fontWeight.medium : theme.typography.fontWeight.regular,
            fontSize: theme.typography.fontSize.xs,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {isDragActive ? 'Drop files...' : 'Drop/Click'}
        </Typography>
      </Box>
    </Box>
  );
}